import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Modal, Spinner } from 'react-bootstrap';
import AsyncSelect from 'react-select/async';
import toast from 'react-hot-toast';
import { FaArrowRight, FaExclamationTriangle } from 'react-icons/fa';
import { WEFResponse } from '../Types';
import { usePlateTransferEligibilityQuery } from '../queries/plateTransfer.query';
import { useTransferPlateMutation } from '../mutations/plateTransfer.mutation';
import { searchClientsForTransfer } from '../requests/plateTransfer.request';
import { ClientOption, PlateTransferResult } from '../types/plateTransfer.types';

const SEARCH_DEBOUNCE_MS = 300;

type Props = {
  show: boolean;
  plate: { id: string; plateNumber: string; clientId: string };
  onHide: () => void;
  onTransferred: (result: PlateTransferResult) => void;
};

/** Pulls the API's readable message out of a failed request, if it sent one. */
const getApiErrorMessage = (error: unknown): string | null => {
  const data = (error as { response?: { data?: Partial<WEFResponse<unknown>> } })?.response?.data;
  return data?.errors?.length ? data.errors.join(' ') : null;
};

function PlateTransferModal({ show, plate, onHide, onTransferred }: Props) {
  const [destination, setDestination] = useState<ClientOption | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const searchTimer = useRef<ReturnType<typeof setTimeout>>();

  const {
    data: eligibility,
    isLoading: checking,
    isError: checkFailed
  } = usePlateTransferEligibilityQuery(plate.id, show);
  const transferMutation = useTransferPlateMutation();
  const submitting = transferMutation.isLoading;

  useEffect(() => {
    if (show) {
      setDestination(null);
      setSubmitError(null);
    }
    return () => clearTimeout(searchTimer.current);
  }, [show]);

  const loadClientOptions = (inputValue: string) =>
    new Promise<ClientOption[]>((resolve) => {
      clearTimeout(searchTimer.current);
      if (!inputValue.trim()) {
        resolve([]);
        return;
      }
      searchTimer.current = setTimeout(async () => {
        try {
          const options = await searchClientsForTransfer(inputValue.trim());
          // The plate's current client is never a valid destination.
          resolve(options.filter((option) => option.value !== plate.clientId));
        } catch {
          resolve([]);
        }
      }, SEARCH_DEBOUNCE_MS);
    });

  const handleTransfer = () => {
    if (!destination) return;
    setSubmitError(null);
    transferMutation.mutate(
      { plateId: plate.id, request: { destinationClientId: destination.value } },
      {
        onSuccess: (result) => {
          if (!result) return;
          toast.success(`Plate ${result.plateNumber} transferred to ${result.toClientName}.`);
          onTransferred(result);
          onHide();
        },
        onError: (error) => {
          setSubmitError(
            getApiErrorMessage(error) ??
              'The plate could not be transferred. Please try again, or refresh the page if it keeps failing.'
          );
        }
      }
    );
  };

  const canTransfer = !!eligibility?.canTransfer;
  const currentClientName = eligibility?.clientName ?? 'its current client';

  const renderBody = () => {
    if (checking) {
      return (
        <div className="d-flex align-items-center gap-2 text-muted">
          <Spinner animation="border" size="sm" /> Checking whether this plate can be transferred...
        </div>
      );
    }

    if (checkFailed || !eligibility) {
      return (
        <Alert variant="danger" className="mb-0">
          We couldn&apos;t check whether this plate can be transferred. Close this window and try
          again.
        </Alert>
      );
    }

    if (!canTransfer) {
      return (
        <Alert variant="danger" className="mb-0">
          <Alert.Heading as="h6" className="d-flex align-items-center gap-2">
            <FaExclamationTriangle /> This plate can&apos;t be transferred
          </Alert.Heading>
          <div>{eligibility.reason}</div>
          {eligibility.linkedTicketCount > 0 && (
            <div className="mt-2 small">
              You can see these tickets on {currentClientName}&apos;s Tickets tab.
            </div>
          )}
        </Alert>
      );
    }

    return (
      <>
        <p className="mb-2">
          Move this plate from <strong>{currentClientName}</strong> to another client. The change
          happens immediately.
        </p>
        <p className="small text-muted">
          Only the plate&apos;s client changes. Its monitoring dates, registration file and history
          stay with the plate. No invoices, payments or messages are created.
        </p>

        <label htmlFor={`transfer-client-${plate.id}`} className="form-label fw-bold">
          Transfer to
        </label>
        <AsyncSelect<ClientOption, false>
          inputId={`transfer-client-${plate.id}`}
          cacheOptions
          loadOptions={loadClientOptions}
          value={destination}
          onChange={(option) => {
            setDestination(option);
            setSubmitError(null);
          }}
          isClearable
          isDisabled={submitting}
          placeholder="Search clients by name..."
          noOptionsMessage={({ inputValue }) =>
            inputValue.trim() ? 'No other clients match that name' : 'Start typing a client name'
          }
          loadingMessage={() => 'Searching...'}
          menuPortalTarget={document.body}
          styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
        />

        {destination && (
          <Alert variant="info" className="mt-3 mb-0 d-flex align-items-center flex-wrap gap-2">
            <strong>{plate.plateNumber.toUpperCase()}</strong>
            <span>{currentClientName}</span>
            <FaArrowRight />
            <strong>{destination.label}</strong>
          </Alert>
        )}
      </>
    );
  };

  return (
    <Modal show={show} onHide={submitting ? undefined : onHide} centered>
      <Modal.Header closeButton={!submitting}>
        <Modal.Title>Transfer plate {plate.plateNumber.toUpperCase()}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {renderBody()}
        {/* If the re-check now blocks the transfer, the body already explains why. */}
        {submitError && canTransfer && (
          <Alert variant="danger" className="mt-3 mb-0">
            {submitError}
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide} disabled={submitting}>
          {canTransfer ? 'Cancel' : 'Close'}
        </Button>
        {canTransfer && (
          <Button variant="primary" onClick={handleTransfer} disabled={!destination || submitting}>
            {submitting ? <Spinner animation="border" size="sm" /> : 'Transfer plate'}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
}

export default PlateTransferModal;
